import { QueryClient, SetDataOptions, notifyManager } from "@tanstack/react-query";
import {
  ModelName,
  ParamsForMethod,
  QueryDataForMethod,
  QueryMethodForModel,
  TellescopeQueryUpdate,
  TellescopeQueryUpdateWithTimestamp,
} from "../../types";
import { TellescopeMethodQueryFilterConfig, TellescopeUpdaterFn } from "../../hooks";
import { getTellescopeQueriesData } from "./get-tellescope-queries-data";
import { FORCE_INVALIDATE } from "../constants";

/**
 * Arguments for setting multiple queries' data in cache with optional filtering.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type that can be thrown
 */
export interface SetTellescopeQueriesDataArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, updates all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The data to set, or an updater function */
  updater: TellescopeUpdaterFn<Model, Method>;
  /** Optional query filters to narrow down which queries to update */
  filters?: TellescopeMethodQueryFilterConfig<Model, Method, TError>;
  /** Optional set data options (e.g., updatedAt) */
  options?: SetDataOptions;
  /** Whether this update is being applied optimistically (before mutation completes). Defaults to false. */
  isOptimistic?: boolean;
}

/**
 * Return type for setting multiple queries' data in cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 */
export interface SetTellescopeQueriesDataReturn<Model extends ModelName, Method extends QueryMethodForModel<Model>> {
  /** Array of queries that were updated with their original data before the update */
  originalQueries: TellescopeQueryUpdateWithTimestamp<Model, Method>[];
  /** Array of queries that were updated with their new data after the update */
  updatedQueries: TellescopeQueryUpdate<Model, Method>[];
}

/**
 * Sets cached data for all matching queries in the query client.
 * Returns an object containing arrays of [queryKey, data] tuples for all queries that were updated,
 * both with their original data and their new data.
 * For infinite queries, data should be wrapped in InfiniteData structure.
 *
 * **Special Updater Return Values:**
 * - Return `undefined`: Skip the update (no change to cache)
 * - Return `null`: Reset the query (remove from cache) - useful for deletions
 * - Return `FORCE_INVALIDATE`: Invalidate the query (triggers a network refetch)
 * - Return data: Update the cache with the new data
 *
 * @param args - The set queries data arguments
 * @returns An object with originalQueries (before update) and updatedQueries (after update)
 *
 * @example
 * ```tsx
 * // Update all queries for a method
 * const { originalQueries, updatedQueries } = setTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   updater: (old) => old ? { ...old, isActive: true } : old,
 * }); // Updates all getOne queries
 *
 * // Update specific query
 * const result = setTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   updater: { id: "user-123", fname: "Jane", lname: "Doe", email: "jane@example.com" },
 * });
 *
 * // Reset queries for deleted entities
 * setTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "deleted-user-123" },
 *   updater: () => null, // Resets the query
 * });
 *
 * // With filters
 * setTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   updater: (old) => old ? { ...old, lastUpdated: Date.now() } : old,
 *   filters: {
 *     stale: false, // Only update non-stale queries
 *   },
 * });
 *
 * // With custom options
 * setTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   updater: { id: "user-123", fname: "Jane", lname: "Doe", email: "jane@example.com" },
 *   options: {
 *     updatedAt: Date.now(), // Set custom update timestamp
 *   },
 * });
 * ```
 */
export const setTellescopeQueriesData = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  updater,
  filters,
  options,
  isOptimistic = false,
}: SetTellescopeQueriesDataArgs<Model, Method, TError>): SetTellescopeQueriesDataReturn<Model, Method> => {
  // Get all matching queries first
  const matchingQueries = getTellescopeQueriesData<Model, Method, TError>({
    model,
    method,
    request,
    queryClient,
    filters,
  });

  // Iterate through each matching query and update it.
  // Wrapped in notifyManager.batch() so observer notifications are coalesced
  // into a single React render pass when updating multiple queries at once.
  return notifyManager.batch(() =>
    matchingQueries.reduce<SetTellescopeQueriesDataReturn<Model, Method>>(
      (acc, [queryKey, originalData]) => {
        const originalTimestamp = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? Date.now();
        acc.originalQueries.push([queryKey, originalData, originalTimestamp]);

        let shouldReset = false;
        let shouldInvalidate = false;

        const updatedData = queryClient.setQueryData<QueryDataForMethod<Model, Method>>(
          queryKey,
          (old) => {
            if (typeof updater === "function") {
              // @ts-expect-error - TypeScript can't narrow union types when QueryData could itself be a function
              const updatedData = updater(old, { queryKey, isOptimistic });

              if (updatedData === FORCE_INVALIDATE) {
                shouldInvalidate = true;
                return old;
              }

              if (updatedData === null) {
                shouldReset = true;
              }

              return updatedData;
            }
            return updater;
          },
          options
        );

        if (shouldInvalidate) {
          void queryClient.invalidateQueries({ queryKey, exact: true });
          acc.updatedQueries.push([queryKey, undefined]);
        } else if (shouldReset) {
          queryClient.resetQueries({
            queryKey,
            exact: true,
          });
          acc.updatedQueries.push([queryKey, undefined]);
        } else {
          acc.updatedQueries.push([queryKey, updatedData]);
        }

        return acc;
      },
      { originalQueries: [], updatedQueries: [] }
    )
  );
};
